import { Context, createContext, FC, useContext, useMemo } from 'react';
import { createContextualCan } from '@casl/react';
import { AbilityBuilder, AnyAbility, createMongoAbility } from '@casl/ability';
import { TokenProviderContext } from '../auth/components/token-provider.component';
import { CMPPermissions, CMPResourceGroup } from './types/rbac-restriction.enum';

export const RBACProtectionContext: Context<AnyAbility> = createContext(null);
export const Can = createContextualCan(RBACProtectionContext.Consumer);

export const RBACProtectionProvider: FC = ({ children }) => {
  const {
    canUserAccessCSM,
    canUserAccessCSMCompany,
    canUserAccessAutomation,
    canUserAccessRequestsCatalog,
    canUserAccessReports,
    canUserAccessPublisherUpdates,
    canUserAccessPublisherUpdateTabMyContracts,
    canUserAccessMyProjects,
    canUserRaiseCase,
    isFullyAuthenticated,
    isTenantAdmin,
    isPlatformAdmin,
    isPortalAdmin,
    scus,
  } = useContext(TokenProviderContext);

  const ability = useMemo(() => {
    const { can, build } = new AbilityBuilder(createMongoAbility);

    if (!isFullyAuthenticated) {
      // Cannot access anything, yet -> auth process happening in background
      return build();
    }

    // feature - error pages, info pages, help pages...
    can(CMPPermissions.FullAccess, CMPResourceGroup.General);
    if (canUserAccessRequestsCatalog) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.RequestsCatalog);
    }

    if (scus.length === 0) {
      // So far can only access generic pages and CIAM-provided entry points.
      return build();
    }

    if (isTenantAdmin || isPlatformAdmin || isPortalAdmin) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.CIAM);
    }

    /*
     * This long block below describes the access per features
     * and they are stacking more access or restrictions
     * as going down the list.
     *
     * Starting with the main parts of the application
     * */

    // feature - home
    can(CMPPermissions.FullAccess, CMPResourceGroup.CmpWithSCU);

    // feature - cases
    if (canUserAccessCSM) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.SupportSystem);
      can(CMPPermissions.FullAccess, CMPResourceGroup.CasesIndividual);
      if (canUserAccessCSMCompany) {
        can(CMPPermissions.FullAccess, CMPResourceGroup.CasesCompany);
      }
      if (canUserRaiseCase) {
        can(CMPPermissions.FullAccess, CMPResourceGroup.RaiseCase);
      }
    }

    // feature - reports
    if (canUserAccessReports) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.Reports);
    }

    // feature - automation
    if (canUserAccessAutomation) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.Automation);
      can(CMPPermissions.FullAccess, CMPResourceGroup.AuditLogs);
    }

    // feature - publisher updates
    if (canUserAccessPublisherUpdates) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.PublisherUpdates);

      if (canUserAccessPublisherUpdateTabMyContracts) {
        can(CMPPermissions.FullAccess, CMPResourceGroup.PublisherUpdateTabsMyContracts);
      }
    }

    // feature - my projects
    if (canUserAccessMyProjects) {
      can(CMPPermissions.FullAccess, CMPResourceGroup.MyProjects);
    }

    return build();
  }, [
    canUserAccessCSM,
    canUserAccessCSMCompany,
    canUserAccessRequestsCatalog,
    canUserAccessReports,
    canUserAccessPublisherUpdates,
    canUserAccessPublisherUpdateTabMyContracts,
    canUserAccessMyProjects,
    canUserRaiseCase,
    isFullyAuthenticated,
    canUserAccessAutomation,
    isTenantAdmin,
    isPlatformAdmin,
    isPortalAdmin,
    scus,
  ]);

  return (
    <RBACProtectionContext.Provider value={ability}>{children}</RBACProtectionContext.Provider>
  );
};
